import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  ObfuscationService,
  ObfuscationType,
} from '@principle-theorem/ng-management-tools';
import {
  DownloadReportComponent,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  CustomChartType,
  type ICustomReportChart,
  type ICustomReportChartSection,
} from '@principle-theorem/principle-core/interfaces';
import { ReportBuilderHelpers } from '@principle-theorem/reporting';
import { IReportBuilderDataSource } from '@principle-theorem/reporting/interfaces';
import { snapshot } from '@principle-theorem/shared';
import { type Crossfilter } from 'crossfilter2';
import { compact } from 'lodash';
import { ReplaySubject, combineLatest, type Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { type LabelTransformFn } from '../../../../models/report/charts/data-transformers/dc-data-transformer';
import { ReportBuilderStore } from '../../report-builder.store';
import {
  getChartDisplay,
  type IChartDisplay,
} from './report-builder-chart/report-builder-chart-display';
import { ReportBuilderChartComponent } from './report-builder-chart/report-builder-chart.component';
import {
  ReportBuilderTableChart,
  ReportBuilderTableChartCSV,
} from './report-builder-table-chart/report-builder-table-chart-csv';

@Component({
  selector: 'pr-report-builder-chart-section',
  templateUrl: './report-builder-chart-section.component.html',
  styleUrls: ['./report-builder-chart-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DownloadReportComponent,
    MatButtonModule,
    MatIcon,
    MatTooltip,
    NgCommonModule,
    ReportBuilderChartComponent,
  ],
})
export class ReportBuilderChartSectionComponent {
  private _obfuscation = inject(ObfuscationService);
  section$ = new ReplaySubject<ICustomReportChartSection>(1);
  displays$: Observable<IChartDisplay[]>;
  isTable$: Observable<boolean>;
  @Output() addChartAtIndex = new EventEmitter<{ index: number }>();

  @Input()
  set section(section: ICustomReportChartSection) {
    if (section) {
      this.section$.next(section);
    }
  }

  constructor(public store: ReportBuilderStore) {
    const labelTransformFn = this._buildLabelTransformFn();
    this.displays$ = combineLatest([
      this.section$,
      this.store.ndx$,
      this._obfuscation.isActive$,
    ]).pipe(
      withLatestFrom(this.store.dataSource$),
      map(([[section, ndx, _isActive], dataSource]) =>
        chartsToDisplays(section.charts, dataSource, ndx, labelTransformFn)
      )
    );
  }

  async deleteSection(): Promise<void> {
    const currentSection = await snapshot(this.section$);
    const current = await snapshot(this.store.display$);
    if (!current) {
      return;
    }
    this.store.setDisplay({
      ...current,
      chartSections: current.chartSections.filter(
        (section) => section.uid !== currentSection.uid
      ),
    });
  }

  getDefaultSectionName(
    data: [ICustomReportChartSection, IChartDisplay]
  ): string | undefined {
    const [section, display] = data;
    return ReportBuilderHelpers.getSectionFromPrefix(
      display.chart.groupBy,
      section.name
    );
  }

  isTable(display: IChartDisplay): boolean {
    return display.chart.type === CustomChartType.Table;
  }

  getCSVData(display: IChartDisplay): object[] {
    return ReportBuilderTableChart.getCSVData(display);
  }

  getCSVTranslator$(
    display: IChartDisplay
  ): Observable<ReportBuilderTableChartCSV | undefined> {
    return this.store.dataSource$.pipe(
      map((dataSource) =>
        ReportBuilderTableChart.getCSVTranslator$(display, dataSource)
      )
    );
  }

  private _buildLabelTransformFn(): LabelTransformFn {
    return (label: string, piiType?: string): string =>
      this._obfuscation.transform(
        label,
        (piiType as ObfuscationType) ?? 'name'
      );
  }
}

export function chartsToDisplays(
  charts: ICustomReportChart[],
  dataSource?: IReportBuilderDataSource,
  ndx?: Crossfilter<unknown>,
  labelTransformFn?: LabelTransformFn
): IChartDisplay[] {
  if (!dataSource) {
    return [];
  }
  return compact(
    charts.map((chart) =>
      getChartDisplay(dataSource, chart, ndx, labelTransformFn)
    )
  );
}
